import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import {
  fetchCatalogSnapshot,
  fetchCatalogStatus,
  fetchProductionPendingCatalog,
  normalizeSortOrders,
  publishCatalog,
  seedCatalogCollections,
  promotePublicCatalogToProduction,
  type ProductionPendingResult,
  type PromoteProductionMode,
  SEED_COLLECTIONS,
} from "../lib/catalogApi";
import type { CatalogSnapshot } from "../lib/catalogTypes";
import seed from "../catalog/seed.json";
import { isVectorAssetURL } from "../lib/catalogImages";

const PENDING_LABELS: Record<string, string> = {
  CatalogMeta: "Catalog meta",
  Institution: "Institutions",
  Network: "Networks",
  CardType: "Card types",
  AccountType: "Account types",
  CardProduct: "Cards",
  MerchantIcon: "Merchants",
  InvestmentAsset: "Investment assets",
};

export function CatalogHomePage() {
  const [status, setStatus] = useState<{
    cloudKit: boolean;
    r2: boolean;
    appleAuth: boolean;
  } | null>(null);
  const [snapshot, setSnapshot] = useState<(CatalogSnapshot & { configured?: boolean }) | null>(
    null,
  );
  const [pending, setPending] = useState<ProductionPendingResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const [s, snap, prodPending] = await Promise.all([
        fetchCatalogStatus(),
        fetchCatalogSnapshot(),
        fetchProductionPendingCatalog().catch(() => null),
      ]);
      setStatus(s);
      setSnapshot(snap);
      setPending(prodPending);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load catalog");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function promoteToProduction(mode: PromoteProductionMode) {
    const isFullSync = mode === "all";
    const pendingCount = pending?.total ?? 0;
    const pendingNew = pending?.totalNew ?? 0;
    const pendingChanged = pending?.totalChanged ?? 0;
    const confirmMessage = isFullSync
      ? "Force-sync every Development Public catalog record to Production?\n\nMatching record names in Production will be overwritten. Private iCloud ledgers are never touched."
      : pendingCount === 0
        ? "Nothing to promote — Development and Production fields already match.\n\nPromote anyway?"
        : `Promote ${pendingCount} change${pendingCount === 1 ? "" : "s"} to Production (${pendingNew} new, ${pendingChanged} updated)?\n\nMissing record names are created; shared names with different fields are overwritten. Identical rows are left alone. CatalogMeta is refreshed so TestFlight builds see the latest contentVersion.`;

    if (!window.confirm(confirmMessage)) return;

    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const result = await promotePublicCatalogToProduction(mode);
      const skipped =
        result.skipped.length === 0
          ? ""
          : ` Skipped: ${result.skipped.map((row) => `${row.recordType} (${row.reason})`).join("; ")}.`;
      const copied = Object.entries(result.copied)
        .filter(([, n]) => n > 0)
        .map(([type, n]) => `${type} ${n}`)
        .join(", ");
      const createdTotal = Object.values(result.created ?? {}).reduce((sum, n) => sum + n, 0);
      const updatedTotal = Object.values(result.updated ?? {}).reduce((sum, n) => sum + n, 0);
      const breakdown =
        result.total > 0 && (createdTotal > 0 || updatedTotal > 0)
          ? ` ${createdTotal} new, ${updatedTotal} updated.`
          : "";
      const unchanged = Object.entries(result.unchanged)
        .filter(([, n]) => n > 0)
        .map(([type, n]) => `${type} ${n}`)
        .join(", ");
      const unchangedNote = unchanged ? ` Unchanged: ${unchanged}.` : "";
      setMessage(
        result.total === 0
          ? `No records copied (${result.mode} mode).${unchangedNote}${skipped}`
          : `Promoted ${result.total} Public record${result.total === 1 ? "" : "s"} to Production (${copied || "none"}).${breakdown}${unchangedNote}${skipped}`,
      );
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Promote failed");
    } finally {
      setBusy(false);
    }
  }

  async function run(action: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await action();
      setMessage(ok);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  const pendingRows = pending
    ? Object.entries(pending.pending).filter(([, count]) => count > 0)
    : [];

  const missingArtCount =
    snapshot?.products.filter(
      (product) => !product.cardImageURL || isVectorAssetURL(product.cardImageURL),
    ).length ?? 0;

  return (
    <AppShell>
      <main className="page">
        <div className="page-header">
          <div>
            <span className="overline">CloudKit Public</span>
            <h1>Catalog overview</h1>
            <p>
              Normalized Institutions, Networks, Types, and Card Products in the Public DB. Images
              on R2. Cosmetic data only.
            </p>
          </div>
          <div className="row-actions">
            <button
              className="btn btn-brass"
              type="button"
              disabled={busy}
              onClick={() =>
                void run(
                  () => normalizeSortOrders(),
                  "All catalog sort orders updated to ascending A–Z and published.",
                )
              }
              title="Re-sequence every collection alphabetically in increments of 10"
            >
              <Icon name="check" size={15} />
              Sort all A–Z
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy}
              onClick={() => void reload()}
            >
              <Icon name="refresh" size={15} />
              Refresh
            </button>
            <button
              className="btn btn-primary"
              type="button"
              disabled={busy}
              onClick={() => void run(() => publishCatalog(), "Published — contentVersion bumped.")}
            >
              <Icon name="upload" size={15} />
              Publish
            </button>
          </div>
        </div>

        {error && <div className="banner error">{error}</div>}
        {message && <div className="banner success">{message}</div>}

        <section className="panel grid-3">
          <StatusCard
            title="CloudKit"
            ok={Boolean(status?.cloudKit)}
            detail={status?.cloudKit ? "Server key configured" : "Set Worker secrets"}
          />
          <StatusCard
            title="R2 images"
            ok={Boolean(status?.r2)}
            detail={status?.r2 ? "CARD_IMAGES + public URL" : "Bind bucket + R2_PUBLIC_BASE_URL"}
          />
          <StatusCard
            title="Admin gate"
            ok={Boolean(status?.appleAuth)}
            detail={
              status?.appleAuth
                ? "Sign in with Apple + allowlist"
                : "Set APPLE_CLIENT_ID / SESSION_SECRET"
            }
          />
        </section>

        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="overline">Snapshot</span>
              <h2>Published state</h2>
            </div>
          </div>
          <dl className="kv">
            <div>
              <dt>Schema version</dt>
              <dd>{snapshot?.schemaVersion ?? "—"}</dd>
            </div>
            <div>
              <dt>Content version</dt>
              <dd>{snapshot?.contentVersion ?? "—"}</dd>
            </div>
            <div>
              <dt>Published at</dt>
              <dd>
                {snapshot?.publishedAt ? new Date(snapshot.publishedAt).toLocaleString() : "—"}
              </dd>
            </div>
            <div>
              <dt>Fetched at</dt>
              <dd>{snapshot?.fetchedAt ? new Date(snapshot.fetchedAt).toLocaleString() : "—"}</dd>
            </div>
          </dl>
          <div className="stat-row">
            <Link to="/institutions">
              Institutions <span className="count">{snapshot?.institutions.length ?? 0}</span>
            </Link>
            <Link to="/networks">
              Networks <span className="count">{snapshot?.networks.length ?? 0}</span>
            </Link>
            <Link to="/card-types">
              Card types <span className="count">{snapshot?.cardTypes.length ?? 0}</span>
            </Link>
            <Link to="/account-types">
              Account types <span className="count">{snapshot?.accountTypes.length ?? 0}</span>
            </Link>
            <Link to="/products">
              Cards <span className="count">{snapshot?.products.length ?? 0}</span>
            </Link>
            <Link to="/merchant-icons">
              Merchants <span className="count">{snapshot?.merchantIcons.length ?? 0}</span>
            </Link>
            <Link to="/investment-assets">
              Investment assets{" "}
              <span className="count">{snapshot?.investmentAssets?.length ?? 0}</span>
            </Link>
          </div>
          <div className="stat-row" style={{ marginTop: "0.75rem" }}>
            <span className="badge ok">
              Institutions {snapshot?.institutions.length ?? 0}
            </span>
            <span className={`badge ${missingArtCount > 0 ? "warn" : "ok"}`}>
              Cards missing art {missingArtCount}
            </span>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="overline">Environments</span>
              <h2>Promote Development → Production</h2>
            </div>
          </div>
          <p className="muted">
            Edit and publish in <strong>Development</strong> first. Then promote{" "}
            <strong>new records and field edits</strong> to Production without a full rewrite.
            Artwork stays on R2 (same URLs). This cannot copy private iCloud ledgers. Deploy the
            Production schema in CloudKit Dashboard first.
          </p>
          {pending && (
            <dl className="kv" style={{ marginBottom: "1rem" }}>
              <div>
                <dt>Pending in Production</dt>
                <dd>
                  <strong>{pending.total}</strong>
                  {pending.total === 0
                    ? " — in sync"
                    : ` (${pending.totalNew ?? 0} new · ${pending.totalChanged ?? 0} updated)`}
                </dd>
              </div>
              {pendingRows.length > 0 && (
                <div>
                  <dt>By type</dt>
                  <dd>
                    {pendingRows
                      .map(([type, count]) => {
                        const neu = pending.pendingNew?.[type] ?? 0;
                        const chg = pending.pendingChanged?.[type] ?? 0;
                        const detail =
                          neu > 0 || chg > 0 ? ` (${neu} new, ${chg} updated)` : "";
                        return `${PENDING_LABELS[type] ?? type}: ${count}${detail}`;
                      })
                      .join(" · ")}
                  </dd>
                </div>
              )}
            </dl>
          )}
          <div className="row-actions">
            <button
              className="btn btn-primary"
              type="button"
              disabled={busy}
              onClick={() => void promoteToProduction("new")}
              title="Copy missing records and overwrite Production rows whose fields differ"
            >
              Promote changes to Production
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy}
              onClick={() => void promoteToProduction("all")}
              title="Force-upsert every Development record — use for a full rewrite"
            >
              Full sync to Production
            </button>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="overline">Bootstrap</span>
              <h2>Seed</h2>
            </div>
          </div>
          <p className="muted">
            Seed writes reference Institutions / Networks / Types / PH products, then publishes. Each
            collection is applied in its own request, so a large seed stays inside the Worker
            subrequest limit.
          </p>
          <div className="row-actions">
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy}
              onClick={() =>
                void run(
                  () => seedCatalogCollections(seed as never, SEED_COLLECTIONS),
                  "Seed applied and published.",
                )
              }
            >
              Apply seed.json
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy}
              onClick={() =>
                void run(
                  () => seedCatalogCollections(seed as never, ["investmentAssets"]),
                  `Investment assets applied and published (${seed.investmentAssets?.length ?? 0} in seed.json).`,
                )
              }
              title="Write only the InvestmentAsset rows from seed.json"
            >
              Apply investment assets only
            </button>
          </div>
        </section>
      </main>
    </AppShell>
  );
}

function StatusCard({ title, ok, detail }: { title: string; ok: boolean; detail: string }) {
  return (
    <div className={`status-card ${ok ? "ok" : "warn"}`}>
      <span className="overline">{title}</span>
      <strong>
        <span className="dot" />
        {ok ? "Ready" : "Needs setup"}
      </strong>
      <p className="muted" style={{ margin: 0, fontSize: "0.83rem" }}>
        {detail}
      </p>
    </div>
  );
}
